/* =========================================================================
   Pilgrimage 1 "Learning the Faith" — map screen + client-side gate engine.

   Gates evaluate through the ONE canonical CriteriaJson evaluator (criteria.ts),
   fed by the SAME event stream the quest engine consumes: the engine subscribes
   via onQuestEvent() (quests.ts) — the single-observer seam quests.ts owns — so
   there is no second telemetry observer. Every matching event advances that
   step's counter (persisted at 'catholics.pilgrimage'); a step auto-completes
   when its gate is satisfied AND all prior steps are done (mirroring the server's
   usp_PilgrimageStep_Complete ordering rule).

   Completion grants the step's collectible (Apostle) + any item locally, fires
   Theo's 'p1-step' pop + PilgrimageStepDone, and (authed) calls
   completePilgrimageStep — a 409 PriorStepIncomplete triggers a server resync.
   CENTS rewards are DEFERRED to the server (granted by the proc for accounts);
   in-client v1.1.0 never mints cents outside the game engine (brief §3 ruling).
   ========================================================================= */
import { esc } from './engine';
import { icon } from './ui/icons';
import { byId, show } from './dom';
import { track } from './telemetry';
import { getLine } from './characters/registry';
import { triggerCharPop } from './characters/ui';
import { onQuestEvent } from './quests';
import { earnCollectible } from './collectibles';
import { grantItem } from './inventory';
import { getPilgrimage, completePilgrimageStep, isAuthenticated } from './api';
import type { Pilgrimage, PilgrimageStep } from './api';
import { BUNDLED_P1 } from './data/pilgrimageData';
import { parseCriteria, eventMatchesCriteria, gateSatisfied, countTarget } from './criteria';
import { isPilgrimageEnabled, hasOptionalMedia } from './featureFlags';
import type { KVStore } from './dailyBlessing';

const STORE_KEY = 'catholics.pilgrimage';
const PILG_KEY = 'p1';

/** Some gate triggers are server-side event names; bridge them to the client
    telemetry key that carries the same beat. */
const TRIGGER_ALIAS: Record<string, string> = { GameCompleted: 'SessionCompleted' };

interface PilgState {
  completed: Record<string, string>; // stepKey → ISO
  counts: Record<string, number>; // stepKey → matched-event count (capped at target)
  introSeen?: boolean;
}

/* -------------------------------- storage -------------------------------- */
function deviceStore(): KVStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
function readState(store: KVStore | null = deviceStore()): PilgState {
  try {
    const raw = store?.getItem(STORE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as PilgState;
      if (p && typeof p === 'object') return { completed: p.completed || {}, counts: p.counts || {}, introSeen: p.introSeen };
    }
  } catch {
    /* ignore */
  }
  return { completed: {}, counts: {} };
}
function writeState(s: PilgState, store: KVStore | null = deviceStore()): void {
  try {
    store?.setItem(STORE_KEY, JSON.stringify(s));
  } catch {
    /* private mode — progress won't persist */
  }
}

/* ------------------------------- pure logic ------------------------------- */

/** The earliest incomplete step whose gate is satisfied AND whose prior steps
    are all complete — or null when the frontier step isn't ready yet (steps
    complete strictly in order). PURE. */
export function firstCompletable(
  steps: PilgrimageStep[],
  completed: Set<string>,
  counts: Record<string, number>,
): PilgrimageStep | null {
  for (const step of steps) {
    if (completed.has(step.key)) continue;
    // First incomplete step = the frontier; it's completable only if its own gate is met.
    const c = parseCriteria(step.gateCriteriaJson);
    if (c && gateSatisfied(c, counts[step.key] || 0)) return step;
    return null; // frontier not ready → nothing later can complete first
  }
  return null;
}

/** Human requirement label for a step (used in the node popover). PURE. */
export function requirementText(step: PilgrimageStep): string {
  const c = parseCriteria(step.gateCriteriaJson);
  if (!c) return 'Keep walking the pilgrimage.';
  const cat = c.conditions.find((x) => x.metric === 'Category');
  const n = countTarget(c);
  switch (c.trigger) {
    case 'QuestionAnswered':
      return cat ? `Answer ${n} ${String(cat.value)} questions.` : `Answer ${n} questions.`;
    case 'SessionCompleted':
      return 'Finish a full game.';
    case 'ArcadePlayed':
      return 'Play one arcade game.';
    case 'FeastVisited':
      return "Visit today's feast moment.";
    case 'GtkyAnswered':
      return 'Answer one of Theo’s get-to-know-you questions.';
    case 'GalleryPurchased': {
      const item = c.conditions.find((x) => x.metric === 'ItemKey');
      return item?.value === 'chapel_altar'
        ? 'Restore the Chapel Altar in the Monastery.'
        : 'Add a piece to the Monastery.';
    }
    default:
      return 'Keep walking the pilgrimage.';
  }
}

/* --------------------------------- state --------------------------------- */
let pilg: Pilgrimage = BUNDLED_P1;
let loaded = false;
let settling = false;

function stepByKey(key: string): PilgrimageStep | undefined {
  return pilg.steps.find((s) => s.key === key);
}

/* ------------------------- read-only seams (Path v1) -------------------------
   The home path surface (pathHome.ts) PRESENTS this engine; it never advances a
   gate or grants a reward. It reads the live frontier through pilgrimageState()
   and repaints on setPilgrimageChangeObserver() — the same single-observer shape
   quests.ts uses for the quest card. Kept here (not exported state) so the
   engine stays the only writer.
   ---------------------------------------------------------------------------- */

export interface PilgrimageView {
  pilg: Pilgrimage;
  /** Step keys completed on this device (server completions merged in on load). */
  completed: Set<string>;
  /** stepKey → matched-event count (capped at the gate's target). */
  counts: Record<string, number>;
}

/** Live snapshot of the pilgrimage for read-only surfaces. */
export function pilgrimageState(): PilgrimageView {
  const s = readState();
  return { pilg, completed: new Set(Object.keys(s.completed)), counts: { ...s.counts } };
}

let changeObserver: (() => void) | null = null;

/** Single observer notified whenever the frontier or a gate counter moves (a
    step completed, a gate ticked, or the server resynced). */
export function setPilgrimageChangeObserver(fn: (() => void) | null): void {
  changeObserver = fn;
}

function notifyChange(): void {
  try {
    changeObserver?.();
  } catch {
    /* a broken observer must never break the engine */
  }
}

/** Grant a completed step's rewards locally (collectible + item; cents deferred),
    fire telemetry + Theo pop, and record server-side for accounts. */
function completeStep(step: PilgrimageStep): void {
  const state = readState();
  if (state.completed[step.key]) return;
  state.completed[step.key] = new Date().toISOString();
  writeState(state);

  // Rewards: collectible (Apostle) + item are earned LOCALLY (offline-safe);
  // cents are granted server-side by usp_PilgrimageStep_Complete for accounts.
  try {
    const reward = step.rewardJson ? (JSON.parse(step.rewardJson) as { collectible?: string; item?: string; qty?: number }) : null;
    if (reward?.collectible) earnCollectible(reward.collectible);
    if (reward?.item) grantItem(reward.item, reward.qty ?? 1, 'pilgrimage');
  } catch {
    /* malformed reward JSON — skip local grants */
  }

  track('PilgrimageStepDone', { key: step.key });
  notifyChange(); // home path CTA advances to the next station
  try {
    triggerCharPop(getLine('theo', 'p1-step'));
  } catch {
    /* no DOM (tests) */
  }

  // Accounts: record + grant server-side; a 409 (PriorStepIncomplete) means our
  // local view drifted from the server → resync.
  if (isAuthenticated()) {
    void completePilgrimageStep({ stepKey: step.key }).then((r) => {
      if (!r.ok && !r.offline && r.status === 409) void resyncFromServer();
    });
  }
}

/** Run the settle loop: complete every ready frontier step in order. */
function settle(): void {
  if (settling) return;
  settling = true;
  try {
    for (;;) {
      const state = readState();
      const done = new Set(Object.keys(state.completed));
      const next = firstCompletable(pilg.steps, done, state.counts);
      if (!next) break;
      completeStep(next);
    }
  } finally {
    settling = false;
  }
  if (isVisiblePilg()) render();
}

/** onQuestEvent handler: accumulate matching counts for incomplete steps, then
    settle. Registered once at boot so gates progress from normal play. */
function onEvent(eventKey: string, props?: Record<string, unknown>): void {
  const canonical = TRIGGER_ALIAS[eventKey] ?? eventKey;
  const state = readState();
  const done = new Set(Object.keys(state.completed));
  let changed = false;
  for (const step of pilg.steps) {
    if (done.has(step.key)) continue;
    const c = parseCriteria(step.gateCriteriaJson);
    if (!c) continue;
    if (eventMatchesCriteria(c, canonical, props)) {
      const target = countTarget(c);
      const cur = state.counts[step.key] || 0;
      if (cur < target) {
        state.counts[step.key] = cur + 1;
        changed = true;
      }
    }
  }
  if (changed) writeState(state);
  settle();
  if (changed) notifyChange(); // gate counter moved → refresh the home "4 / 15" line
}

/** Merge server completion + intro state into local (server completions win). */
async function loadPilgrimage(): Promise<void> {
  const r = await getPilgrimage(PILG_KEY);
  if (r.ok && r.data && Array.isArray(r.data.steps) && r.data.steps.length > 0) {
    pilg = r.data;
    const state = readState();
    let changed = false;
    for (const s of r.data.steps) {
      if (s.completedUtc && !state.completed[s.key]) {
        state.completed[s.key] = s.completedUtc;
        changed = true;
      }
    }
    if (changed) writeState(state);
  } else {
    pilg = BUNDLED_P1;
  }
  loaded = true;
  notifyChange(); // the frontier may have moved (server completions merged in)
}

async function resyncFromServer(): Promise<void> {
  await loadPilgrimage();
  if (isVisiblePilg()) render();
}

function isVisiblePilg(): boolean {
  try {
    return !byId('screenPilgrimage').classList.contains('hidden');
  } catch {
    return false;
  }
}

/* -------------------------------- rendering -------------------------------- */
let mapBgAvail = false;
let introMediaAvail = false;

function probeMedia(): void {
  if (!hasOptionalMedia()) return; // p1-map.png / p1-intro.mp4 not installed yet
  const img = new Image();
  img.onload = () => {
    mapBgAvail = true;
    if (isVisiblePilg()) render();
  };
  img.onerror = () => {};
  img.src = 'media/p1-map.png';
  try {
    const v = document.createElement('video');
    v.muted = true;
    v.preload = 'metadata';
    v.onloadeddata = () => {
      introMediaAvail = true;
    };
    v.onerror = () => {};
    v.src = 'media/p1-intro.mp4';
  } catch {
    /* no video support */
  }
}

/** Node lifecycle state for styling. */
function nodeStates(): Map<string, 'done' | 'current' | 'future'> {
  const state = readState();
  const done = new Set(Object.keys(state.completed));
  const m = new Map<string, 'done' | 'current' | 'future'>();
  let currentAssigned = false;
  for (const step of pilg.steps) {
    if (done.has(step.key)) {
      m.set(step.key, 'done');
    } else if (!currentAssigned) {
      m.set(step.key, 'current');
      currentAssigned = true;
    } else {
      m.set(step.key, 'future');
    }
  }
  return m;
}

function render(): void {
  const body = document.getElementById('pilgBody');
  if (!body) return;
  const states = nodeStates();
  const state = readState();
  const doneCount = Object.keys(state.completed).length;

  const sub = document.getElementById('pilgSub');
  if (sub) sub.textContent = `${doneCount} of ${pilg.steps.length} steps · gather the Twelve Apostles`;

  const mapClass = mapBgAvail ? 'pilg-map has-art' : 'pilg-map';
  const mapStyle = mapBgAvail ? `style="background-image:url('media/p1-map.png')"` : '';

  // Connective path (simple polyline between node centers) + nodes.
  const nodes = pilg.steps
    .map((step, i) => {
      const st = states.get(step.key) || 'future';
      const c = parseCriteria(step.gateCriteriaJson);
      const prog = c && c.conditions.some((x) => x.metric === 'Count') ? `${Math.min(countTarget(c), state.counts[step.key] || 0)}/${countTarget(c)}` : '';
      const label = st === 'done' ? '✓' : String(i + 1);
      return (
        `<button class="pilg-node ${st}" type="button" data-step="${esc(step.key)}" ` +
        `style="left:${step.nodeX}%;top:${step.nodeY}%" aria-label="${esc(step.title)}">` +
        `<span class="pn-dot">${label}</span>` +
        `<span class="pn-name">${esc(step.title)}${prog ? ` <span class="pn-prog">${prog}</span>` : ''}</span>` +
        `</button>`
      );
    })
    .join('');

  body.innerHTML = `<div class="${mapClass}" ${mapStyle}>${nodes}</div>`;

  body.querySelectorAll<HTMLButtonElement>('.pilg-node').forEach((btn) => {
    btn.onclick = () => showNodeInfo(btn.dataset.step || '');
  });
}

function showNodeInfo(stepKey: string): void {
  const step = stepByKey(stepKey);
  if (!step) return;
  const state = readState();
  const done = !!state.completed[step.key];
  const st = nodeStates().get(step.key) || 'future';
  const c = parseCriteria(step.gateCriteriaJson);

  byId('pniIc').innerHTML = done ? icon('check', 28) : st === 'current' ? icon('compass', 28) : icon('lock', 28);
  byId('pniTitle').textContent = step.title;
  byId('pniReq').textContent = done
    ? 'Completed. Well walked, friend.'
    : st === 'future'
      ? `Finish the earlier steps first. Then: ${requirementText(step)}`
      : requirementText(step);
  const prog = byId('pniProg');
  if (!done && c && c.conditions.some((x) => x.metric === 'Count')) {
    prog.textContent = `Progress: ${Math.min(countTarget(c), state.counts[step.key] || 0)} / ${countTarget(c)}`;
    prog.classList.remove('hidden');
  } else {
    prog.textContent = '';
    prog.classList.add('hidden');
  }
  const pop = byId('pilgNodeInfo');
  pop.classList.remove('hidden');
  pop.setAttribute('aria-hidden', 'false');
}
function closeNodeInfo(): void {
  const pop = byId('pilgNodeInfo');
  pop.classList.add('hidden');
  pop.setAttribute('aria-hidden', 'true');
}

/* -------------------------------- intro -------------------------------- */
function maybeShowIntro(): void {
  const state = readState();
  const overlay = byId('pilgIntro');
  if (state.introSeen) {
    overlay.classList.add('hidden');
    return;
  }
  byId('pilgIntroTheo').textContent = getLine('theo', 'p1-intro');
  const media = byId('pilgIntroMedia');
  if (introMediaAvail) {
    media.innerHTML = `<video src="media/p1-intro.mp4" autoplay playsinline muted class="pilg-intro-video"></video>`;
  } else {
    media.innerHTML = `<div class="pilg-intro-fallback">${icon('compass', 64)}</div>`;
  }
  overlay.classList.remove('hidden');
  overlay.setAttribute('aria-hidden', 'false');
}
/** Leave the intro panel. `markSeen` (Begin) records it so it won't re-open; Not
    now leaves it unseen (it'll greet the player again next visit) and returns
    home. Either way the panel never blocks — the ← Home back is always above it. */
function leaveIntro(markSeen: boolean): void {
  if (markSeen) {
    const state = readState();
    state.introSeen = true;
    writeState(state);
  }
  const overlay = byId('pilgIntro');
  overlay.classList.add('hidden');
  overlay.setAttribute('aria-hidden', 'true');
  try {
    overlay.querySelector('video')?.pause();
  } catch {
    /* noop */
  }
}

/* -------------------------------- public -------------------------------- */
export async function openPilgrimage(): Promise<void> {
  track('AreaOpened', { area: 'pilgrimage' });
  byId('bankbar').classList.add('hidden');
  show('pilgrimage');
  if (!loaded) await loadPilgrimage();
  render();
  maybeShowIntro();
}

export function initPilgrimage(): void {
  probeMedia();
  // Subscribe at boot so gates accumulate from normal play, screen open or not.
  onQuestEvent(onEvent);
  byId('pilgBackBtn').addEventListener('click', () => {
    byId('bankbar').classList.add('hidden');
    show('home');
  });
  byId('pniClose').addEventListener('click', closeNodeInfo);
  byId('pilgNodeInfo').addEventListener('click', (e) => {
    if (e.target === byId('pilgNodeInfo')) closeNodeInfo();
  });
  byId('pilgIntroBegin').addEventListener('click', () => leaveIntro(true));
  byId('pilgIntroNotNow').addEventListener('click', () => {
    leaveIntro(false); // quiet exit — no commitment, greet again next time
    byId('bankbar').classList.add('hidden');
    show('home');
  });
  // Kick a background load so the frontier is fresh even before first open — but
  // ONLY when the Pilgrimage is live. While it's paused, skip the fetch so it can't
  // hit /api/pilgrimage/p1 (503 until db/017 runs) on every boot.
  if (isPilgrimageEnabled()) void loadPilgrimage();
}
